import { EntityTypes, Geotab, SearchTypes } from "@fairfleet/geotab";
import { O } from "ts-toolbelt";

export interface FetchAllSortedByIdOptions {
  /** The entity's `RESULTS_LIMIT_*` cap. */
  pageSize: number;
  /** Upper bound on requests, so a huge database cannot keep the page loading forever. */
  maxPages: number;
  propertySelector?: { fields: string[]; isIncluded: boolean };
  signal?: AbortSignal;
}

/**
 * Reads every entity matching `search` with `Get`, one page of `pageSize` at a time, sorted by id.
 *
 * For entities without `GetFeed` (e.g. `User`). Each request after the first passes the last id of
 * the previous page as `sort.offset`; the offset is exclusive, so pages do not overlap. `lastId`
 * is not sent: Geotab rejects it together with `sortBy: "id"` ("Last id can not be used with sort
 * by id."). Verified live on `User` (2026-10-14).
 *
 * Stops at the first page shorter than `pageSize`, or after `maxPages` requests. The result then
 * holds exactly `pageSize * maxPages` rows and more may exist: callers compare the length against
 * that bound to warn about truncation.
 */
export async function fetchAllSortedById<
  TType extends keyof EntityTypes & keyof SearchTypes,
  TEntity = EntityTypes[TType],
  TSearch extends object = SearchTypes[TType],
>(
  geotabClient: Geotab,
  typeName: TType,
  search: O.Partial<TSearch>,
  { pageSize, maxPages, propertySelector, signal }: FetchAllSortedByIdOptions,
): Promise<TEntity[]> {
  const out: TEntity[] = [];
  let offset: string | undefined;

  for (let page = 0; page < maxPages; page++) {
    signal?.throwIfAborted();
    const rows = await geotabClient.call<TEntity[]>(
      "Get",
      {
        typeName,
        search,
        resultsLimit: pageSize,
        sort: { sortBy: "id", sortDirection: "asc", ...(offset !== undefined && { offset }) },
        ...(propertySelector && { propertySelector }),
      },
      signal,
    );
    if (offset !== undefined && (rows[0] as { id?: string } | undefined)?.id === offset) {
      throw new Error(`Get ${typeName} sorted by id treated offset ${offset} as inclusive`);
    }
    out.push(...rows);
    if (rows.length < pageSize) {
      return out;
    }
    const lastId = (rows[rows.length - 1] as { id?: string }).id;
    if (lastId === undefined || lastId === offset) {
      throw new Error(`Get ${typeName} sorted by id did not advance past offset ${offset}`);
    }
    offset = lastId;
  }
  return out;
}
